/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Grid component to dispay all available routes for a particular store.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import CachedIcon from "@mui/icons-material/Cached";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { format, parse } from "date-fns";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import agent from "../../../../../app/api/agent";
import getAxiosError from "../../../../../app/api/axiosError";
import { getRouteDataPerStore } from "../../../../../app/features/dedicatedDelivery/routeSlice";
import { updateUserTokenToInvalid } from "../../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../../app/features/store/store";
import { DdRouteDashboardResponse } from "../../../../../app/models/responseModels/DdRouteResponse";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../../../../components/common/filter/GridDataFilter";
import Loader from "../../../../../components/common/loader/Loader";
import { useSubmitModalWithParameter } from "../../../../../components/common/modalPopup/useSubmitModal";
import { CustomTooltip } from "../../../../../components/common/tooltip/CustomToolTip";
import {
  DD_ROUTE_PER_STORE_COLUMN_NAME,
  DD_ROUTE_PER_STORE_LABEL_NAME,
  StoreRouteProps,
  ddRoutePerStoreGridFilter,
} from "../../../DdTypes";
import { getDefaultMRTOptions } from "../../../DefaultMrtOptions";
import RouteMetrics from "./RouteMetrics";

const RoutesPerStore = (props: StoreRouteProps) => {
  const clientId = props.clientId;
  const storeId = props.storeId;

  // state to keep route data received after call to server
  const { isLoading, routeDataPerStore } = useSelector(
    (state: RootState) => state.dedicatedDeliveryFetchRoute
  );

  // import default grid configuration
  const defaultMRTOptions = getDefaultMRTOptions<DdRouteDashboardResponse>();
  const dispatch = useAppDispatch();

  // state to keep pagination details of grid
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  // flag to show loader when component is making server call.
  const [dataFetchig, setDataFetchig] = useState<boolean>(true);

  // state flag to show loader on the screen when component is busy
  const [exporting, setExporting] = useState<boolean>(false);

  const [resetFilter, setResetFilter] = useState<boolean>(false);

  // state to keep start date
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject().setDate(props.startDate),
    new DateObject().setDate(props.endDate),
  ]);

  // flag to show or hide modal on grid action
  const [openRouteMetricsModal, setOpenRouteMetricsModal] =
    useState<boolean>(false);

  const [filterableColumnsState, setFilterableColumnsState] =
    useState<FilterableColumnsState>({});

  // server call to get routes for a specific storeId and clientId
  const getRoutes = useCallback(
    async (filterableColumnsState: FilterableColumnsState) => {
      try {
        const response = await dispatch(
          getRouteDataPerStore({
            storeId: storeId,
            clientId: clientId,
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            startDate: dateRange[0].format("YYYY-MM-DD"),
            endDate: dateRange[1].format("YYYY-MM-DD"),
            filter: filterableColumnsState,
          })
        ).unwrap();
        if (!response.isSuccess) {
          toast.error(response.message!, {
            toastId: "storeData",
          });
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "storeDataException",
        });
      } finally {
        setDataFetchig(false);
        setResetFilter(false);
      }
    },
    [dispatch, pagination, dateRange, clientId, storeId]
  );

  //incase of flag is true make a server call to get routes associated with client and store
  useEffect(() => {
    if (dataFetchig) {
      getRoutes(filterableColumnsState);
    }
  }, [dataFetchig, getRoutes]);

  //when page changes with in a grid then make a call to server to get data for that page
  useEffect(() => {
    setDataFetchig(true);
  }, [pagination.pageIndex, pagination.pageSize]);

  // required columns to be rendered on
  const columns = useMemo<MRT_ColumnDef<DdRouteDashboardResponse>[]>(
    () => [
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.DATE,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.DATE,
        Cell: ({ cell, row }) =>
          new Date(cell.getValue<string>()).toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.FDCSTOREID,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.FDCSTOREID,
        Cell: ({ cell, row }) => row.original.store.storeId,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.CLIENTSTOREID,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.CLIENTSTOREID,
        Cell: ({ cell, row }) => row.original.store.clientStoreId,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.TRIPID,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.TRIPID,
      },
      {
        header: "Metrice",
        Cell: ({ cell, row }) => (
          <Button
            onClick={() => {
              openRoutesMetricsModal(row.original);
              setOpenRouteMetricsModal(true);
              console.log(row);
            }}
          >
            View Metrics
          </Button>
        ),
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.DRIVERID,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.DRIVERID,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.DRIVERNAME,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.DRIVERNAME,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.TOTALORDERS,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.TOTALORDERS,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.ORDERSDELIVERED,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.ORDERSDELIVERED,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.ORDERSUNDELIVEREDNOATTEMPT,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.ORDERSUNDELIVEREDNOATTEMPT,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.ORDERSUNDELIVEREDATTEMPT,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.ORDERSUNDELIVEREDATTEMPT,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.MILES,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.MILES,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.TRIPACTUALTIME,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.TRIPACTUALTIME,
      },
    ],
    []
  );

  const handleStoreExport = async (routeIds?: number[] | undefined) => {
    try {
      setExporting(true);
      if (dateRange) {
        const formatStartDateP = dateRange[0].format("YYYY-MM-DD");
        const formatEndDateP = dateRange[1].format("YYYY-MM-DD");
        // Send GET request to Spring Boot backend
        const response =
          await agent.DedicatedDeliveryAgent.exportStoreAssociatedRoutesData({
            clientId: props.clientId,
            storeId: props.storeId,
            routeIds: routeIds ?? [],
            startDate: formatStartDateP,
            endDate: formatEndDateP,
            filters: filterableColumnsState,
          });

        // Create a blob from the response data
        const blob = new Blob([response], {
          type: "application/csv",
        });

        // Create a download link and trigger click event
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.download =
          storeId +
          "-Associated-Routes-Report-" +
          formatStartDateP +
          "-to-" +
          formatEndDateP +
          ".csv";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Cleanup
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file:" + error);
      // Handle error
    } finally {
      setExporting(false);
    }
  };

  // elements to be rendered on top of grid
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<DdRouteDashboardResponse>
  ) => {
    return (
      <Box>
        <CustomTooltip
          arrow
          title="Refresh"
          onClick={() => {
            getRoutes(filterableColumnsState);
          }}
        >
          <IconButton type="button">
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          arrow
          title="Export"
          onClick={() => {
            // if no selection made, then all rows based on date filter is exported.
            if (
              !table.getIsSomeRowsSelected() &&
              !table.getIsAllRowsSelected()
            ) {
              handleStoreExport(undefined);
            } else {
              // if selection made, then all selected rows are exported.
              handleStoreExport(
                table.getSelectedRowModel().rows.map((row) => row.original.id)
              );
            }
          }}
        >
          <IconButton type="button">
            <FileDownloadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  // open route metrices modal incase of user click any of the routes
  const [routesMetricsModal, openRoutesMetricsModal] =
    useSubmitModalWithParameter<DdRouteDashboardResponse>(
      (rowData, closeRoutesMetricsModal) => (
        <Modal
          aria-labelledby="new-store-add-modal"
          aria-describedby="new-store-add-modal-description"
          open={openRouteMetricsModal}
          onClose={(e) => {
            setOpenRouteMetricsModal(false);
          }}
          style={{ overflow: "scroll" }}
        >
          <Container
            component={Paper}
            style={{ marginTop: "2%", height: "auto" }}
          >
            <RouteMetrics route={rowData} close={setOpenRouteMetricsModal} />
          </Container>
        </Modal>
      )
    );

  const getValuesByFieldForGridFilter = (
    columnName: string,
    setClFilterableColumns: React.Dispatch<
      React.SetStateAction<FilterableColumns>
    >
  ) => {
    if (columnName) {
      agent.DedicatedDeliveryAgent.getValuesByField(
        dateRange[0].format("YYYY-MM-DD"),
        dateRange[1].format("YYYY-MM-DD"),
        columnName,
        clientId,
        storeId
      )
        .then((res) => {
          let resData = res.data;
          console.log(res.data);
          if (res.isSuccess && resData?.length) {
            resData = resData?.map((e) => e.toString());
            setClFilterableColumns((prevState: FilterableColumns) => {
              return {
                ...prevState,
                [columnName]: {
                  ...prevState[columnName],
                  options: resData ?? [],
                },
              };
            });
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error(
            "Request is failed while loading options, please try again later."
          );
        });
    }
  };

  // grid configruatoin for routes per store
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: routeDataPerStore.routes,
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: routeDataPerStore.totalRows,
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        id: false,
      },
    },
    state: {
      pagination,
      showProgressBars: isLoading,
      sorting: [{ desc: true, id: "date" }],
    },
    muiTableBodyRowProps: ({ row }) => ({
      /* onClick: (e) => {
        console.log(row.original);
      }, */
      ...defaultMRTOptions.muiTableBodyRowProps,
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="body1" component="p">
          Total Rows: {routeDataPerStore.totalRows}
        </Typography>
      );
    },
  });
  return (
    <>
      <Grid container paddingY={2} justifyContent="space-between">
        <Loader isLoading={exporting || dataFetchig || isLoading} />
        <Grid container item alignItems="center" xs={10} paddingTop={1}>
          <Box zIndex={100} style={{ overflowY: "auto" }}>
            <DatePicker
              format="MM/DD/YYYY"
              maxDate={new Date()}
              className="green"
              dateSeparator=" ~ "
              range
              rangeHover
              value={dateRange}
              onChange={(dateObjects: DateObject[]) => {
                setDateRange(dateObjects);
              }}
            />
          </Box>
          <Button
            size="large"
            onClick={() => {
              setDataFetchig(true);
              setResetFilter(true);
            }}
          >
            SUBMIT
          </Button>
        </Grid>
        <Grid item xs={12} paddingTop={2}>
          <GridDataFilter
            filterableColumns={ddRoutePerStoreGridFilter}
            title="Search Store Associated Route Data"
            dateRange={dateRange}
            resetFlag={resetFilter}
            asyncSubmitFunction={(filterState: FilterableColumnsState) => {
              console.log(filterState);
              setFilterableColumnsState(filterState);
              getRoutes(filterState);
            }}
            asyncSelectedColumnValues={getValuesByFieldForGridFilter}
          />
        </Grid>
        <Grid item xs={12} paddingTop={2}>
          <MaterialReactTable table={table} />
        </Grid>
      </Grid>
      {routesMetricsModal}
    </>
  );
};
export default RoutesPerStore;
